import classNames from 'classnames'
import { ReportFilters, useReportData } from '../../../contexts/ReportsContextProvider'
import styles from './PlayerReport.module.css'
import reportStyles from './ReportCommon.module.css'
import { t } from 'i18next'
import { useCallback, useRef, useState } from 'react'
import { Button } from '../../../common/components/button/Button'
import { getInitialsFromName } from '../../GameEditor/components/Chat/helpers'
import { getIcon } from '../../../common/components/icons/utils'
import { dateTimeToDashDateTimeString } from '../../../util/string'
import { PlayerReportFiltersForm } from './PlayerReportFiltersForm'

export const PlayerReport: React.FC = () => {
  const { playerReportData, loading, setFilters } = useReportData()
  const gridRef = useRef<HTMLDivElement>(null)
  const [showFilters, setShowFilters] = useState(false)

  const setFiltersInternal = useCallback(
    (filters: ReportFilters) => {
      setFilters(filters)
    },
    [setFilters],
  )

  const handleMoveGrid = useCallback((evt: any) => {
    if (evt.buttons && gridRef.current) {
      gridRef.current.scrollLeft -= evt.movementX
      gridRef.current.scrollTop -= evt.movementY
    }
  }, [])

  const handleOpenGame = useCallback(
    (gameId: number) => () => {
      window.open(`/game-editor/${gameId}`, '_blank')
    },
    [],
  )

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev)
  }, [])

  return (
    <div className={classNames(styles.container, loading && reportStyles.loading)} id='PDF_DIV'>
      <div className={styles.headerRow}>
        <h1>{t('report.player.title', 'Player report')}</h1>
      </div>
      <Button style={{ marginLeft: 'auto' }} variant='outline-normal' onClick={handleToggleFilters}>
        <span className={classNames(styles.filtersIconContainer, showFilters && styles.filtersOpen)}>
          {getIcon('filter')}
        </span>
        {t('game_summary.filters_button', 'Filters')}
      </Button>
      {showFilters && <PlayerReportFiltersForm onChange={setFiltersInternal} />}
      <div className={styles.mainArea}>
        <div className={styles.mainAreaLeft}>
          <div>
            <div className={styles.subTitle}>{t('report.player.players_title', 'Players')}</div>
            <div className={styles.showingRow}>
              {t('report.player.showing', 'Showing %{amount}', { amount: playerReportData?.allPlayerAccounts.length })}
            </div>
          </div>
          <div className={styles.colorButtons}>
            <div className={styles.colorCompleted}>{t('report.player.completed', 'Completed')}</div>
            <div className={styles.colorInProgress}>{t('report.player.in_progress', 'In progress')}</div>
            <div className={styles.colorNotStarted}>{t('report.player.not_started', 'Not started')}</div>
          </div>
          <div className={styles.playerLabels}>
            {playerReportData?.allPlayerAccounts.map((player, index) => {
              return (
                <div key={index}>
                  <div className={styles.initialsCircle}>
                    {getInitialsFromName(player.split('@')[0].split('.').join(' ')).toUpperCase()}
                  </div>
                  <div className={styles.playerName}>{player.split('#')[0]}</div>
                </div>
              )
            })}
          </div>
        </div>
        <div ref={gridRef} onMouseMove={handleMoveGrid} className={styles.mainAreaRight}>
          <div>
            <div className={styles.subTitle}>{t('report.player.games_title', 'Games')}</div>
            <div className={styles.showingRow}>
              {t('report.player.showing', 'Showing %{amount}', {
                amount: playerReportData?.reportData.filter((rd) => rd.gamerAccounts.length > 0).length,
              })}
            </div>
          </div>
          <div className={styles.gameGrid}>
            {playerReportData?.reportData.map((game, index) => {
              if (game.gamerAccounts.length === 0) return null
              return (
                <div key={index} className={styles.gameColumn}>
                  <div className={styles.gameInfoBox} onClick={handleOpenGame(game.gameId)}>
                    <div className={styles.gameInfoTop}>
                      <img src={game.mapUrl} alt='' />
                    </div>
                    <div className={styles.gameInfoBottom}>
                      <div className={styles.gameTitle}>{game.gameName}</div>
                      <div className={styles.infoRow}>
                        <span>{getIcon('recents')}</span>
                        {dateTimeToDashDateTimeString(game.lastAnswerDate)}
                      </div>
                      <div className={styles.infoRow}>
                        <span>{getIcon('timerGradient')}</span>
                        {game.avgTime}
                      </div>
                      <div className={styles.infoRow}>
                        <span>{getIcon('multipleUsers')}</span>
                        {game.playersCompleted +
                          '/' +
                          game.playerCount +
                          ' ' +
                          t('report.player.completed', 'Completed')}
                      </div>
                    </div>
                  </div>
                  {playerReportData.allPlayerAccounts.map((player, index) => {
                    const playerEmail = player.split('#')[0]
                    const playerData = game.gamerAccounts.find((ga) => ga.playerEmail === playerEmail)
                    if (!playerData) {
                      return <div key={index} className={styles.emptySlot} />
                    }
                    const completionStatusColor =
                      playerData.completedExercises < 1
                        ? styles.colorNotStarted
                        : playerData.gameCompleted
                        ? styles.colorCompleted
                        : styles.colorInProgress
                    return (
                      <div key={index} className={classNames(styles.playerResultBox, completionStatusColor)}>
                        <div>
                          {completionStatusColor === styles.colorCompleted
                            ? t('report.player.completed', 'Completed')
                            : completionStatusColor === styles.colorInProgress
                            ? t('report.player.in_progress', 'In progress')
                            : t('report.player.not_started', 'Not started')}
                        </div>
                        <div className={styles.infoRow}>
                          <span>{getIcon('pinFilled')}</span>
                          <span>{playerData.completedExercises + '/' + game.exerciseCount}</span>
                          <span>{getIcon('scoreGradient')}</span>
                          <span>{playerData.teamPoints}</span>
                          <span>{getIcon('badgeGradient')}</span>
                          <span>{playerData.badgeCount}</span>
                        </div>
                        <div className={styles.infoRow}>{dateTimeToDashDateTimeString(playerData.lastAnswerDate)}</div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
